import * as React from 'preact/compat'
import { Signal, signal, useSignal } from '@preact/signals'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging'
import { captureException, error, loadStylesheet, showModal } from './helpers'
import Modal from '../components/Modal'
import styles from '../../css/sg-alert.pcss.json'
import buttonsCss from '../../css/buttons.pcss.json'
import Api from '../api'

declare global {
  // noinspection JSUnusedGlobalSymbols
  interface Window {
    promptNotificationsPermission: (type: NotificationPermissionRequestType) => Promise<void>
  }
}

let SWregistration: ServiceWorkerRegistration = null;

const firebaseConfig = {
  apiKey: "AIzaSyCDtiUOrApbwmkeQay9AcfWs5GiQTTgacE",
  authDomain: "stopgame-production.firebaseapp.com",
  projectId: "stopgame-production",
  storageBucket: "stopgame-production.appspot.com",
  messagingSenderId: "492853999817",
  appId: "1:492853999817:web:d388e771af44034c617518"
}

export let currentToken: Signal<string | boolean> = signal(false)
// noinspection IncorrectFormatting
const asyncNoop = async () => {}

let app = null
let messaging = null
let getCurrentToken = asyncNoop
let alreadySaved = false

if ('serviceWorker' in navigator) {
  app = initializeApp(firebaseConfig)
  messaging = getMessaging(app)

  getCurrentToken = async () => {
    try {
      if (currentToken.value !== false) {
        return
      }
      currentToken.value = await getToken(messaging, {
        vapidKey: 'BNYRSCeMYjmB24q9vqlGR87IupwpTBpGCizxu7ACiaIHA3jrQ-tQOV6Ng3ZEitR0gSLGRyyY_oO0gV37vlCJqkA',
        serviceWorkerRegistration: SWregistration
      })
      const response = await Api.post({
        url: 'push/check',
        body: {
          token: currentToken
        }
      })
      cachedPermission.value = response.permission
      if (response.permission !== 'granted' && response.permission !== 'denied-by-token') {
        await saveToken()
      } else {
        alreadySaved = true
      }
    } catch (err) {
      captureException('Cloud Messaging', err)
    }
  }

  onMessage(messaging, (payload) => {
    navigator.serviceWorker.getRegistration('/').then(async registration => {
      const notification: NotificationOptions = {}

      const keys = ['body', 'icon', 'badge', 'image', 'requireInteraction'];
      keys.forEach((key) => {
        if (payload.data[key]) {
          notification[key] = payload.data[key]
        }
      })

      await registration.showNotification(
        payload.data.title,
        {
          ...notification,
          tag: payload.messageId,
          data: {
            url: payload.data.url,
          },
        }
      )
    })
  })
}

export let cachedPermission = signal(null)
export const getPermission = async () => {
  if (!await isSupported() || !('serviceWorker' in navigator)) {
    return 'denied'
  }

  const browserPermission = Notification.permission
  if (browserPermission !== 'granted') {
    cachedPermission.value = browserPermission
    return browserPermission
  }
  if (cachedPermission.value !== null) {
    return cachedPermission.value
  }
  await getCurrentToken()
  return cachedPermission.value
}

const saveToken = async () => {
  if (!currentToken.value || (cachedPermission.value === 'granted') && alreadySaved) {
    return false
  }
  const response = await Api.post({
    url: 'push/register',
    body: {
      token: currentToken.value,
    }
  })
  cachedPermission.value = response.permission
  alreadySaved = true
  return true
}

const fetchAndSaveToken = async () => {
  try {
    await getCurrentToken()
    const result = await saveToken()
    if (!result) {
      error('Cloud Messaging', 'No token available! Request permission to generate one.')
    }
  } catch (err) {
    captureException('Cloud Messaging', err)
  }
}

export const unsubscribe = async () => {
  try {
    const response = await Api.post({
      url: 'push/unregister',
      body: {
        token: currentToken.value,
      }
    })
    cachedPermission.value = response.permission
    return cachedPermission.value === 'default'
  } catch (err) {
    captureException('Cloud Messaging', err)
  }
}

export enum NotificationPermissionRequestType {
  comment,
  blog,
  review,
  game_subscription,
  user_subscription,
  comments_subscription,
  fix_suggestion,
  pm,
  blog_subscription
}

const requestPermissions = async () => {
  const permission = await Notification.requestPermission()
  if (permission === 'granted') {
    await fetchAndSaveToken()
  }
}

const NotificationRequestModal = ({ onClose, reason, isConfirmed = false }: {
  onClose: () => void,
  reason: NotificationPermissionRequestType,
  isConfirmed?: boolean
}) => {
  const text: {
    [key in NotificationPermissionRequestType]: [string, string, string]
  } = {
    [NotificationPermissionRequestType.comment]: [
      'Хочешь узнать когда тебе ответят?',
      'Включи push и получай уведомления об ответах на твои комментарии.',
      'при отправке комментариев'
    ],
    [NotificationPermissionRequestType.blog]: [
      'Важна обратная связь от читателей?',
      'Включи push и получай уведомления о новых комментариях к твоим блогам.',
      'при публикации блогов'
    ],
    [NotificationPermissionRequestType.review]: [
      'Важна обратная связь от читателей?',
      'Включи push и получай уведомления о новых комментариях к твоим блогам.',
      'при публикации отзывов'
    ],
    [NotificationPermissionRequestType.game_subscription]: [
      'Активно следишь за игровой индустрией?',
      'Если включишь push мы будем присылать тебе новости и другие обновления по играм из твоей библиотеки.',
      'при добавлении игр в библиотеку'
    ],
    [NotificationPermissionRequestType.user_subscription]: [
      'Хочешь узнавать о новых материалах от любимых авторов?',
      'Включи push-уведомления и тогда они будут приходить к тебе моментально!',
      'при подписке на пользователей'
    ],
    [NotificationPermissionRequestType.comments_subscription]: [
      'Хочешь следить за дискуссией?',
      'Это намного удобнее делать с включенными push-уведомлениями.',
      'при подписке на комменарии'
    ],
    [NotificationPermissionRequestType.fix_suggestion]: [
      'Хочешь точно знать, что мы приняли твою правку?',
      'Включи push и мы уведомим тебя ровно в тот момент, когда она будет принята!',
      'при предложении правок'
    ],
    [NotificationPermissionRequestType.pm]: [
      'Хочешь оперативно отвечать на личные сообщения?',
      'Будет удобнее, если ты включишь push-уведомления.',
      'при отправке личных сообещний'
    ],
    [NotificationPermissionRequestType.blog_subscription]: [
      'Хочешь оперативно узнавать о новых материалах в избранных блогах?',
      'В этом тебе помогут наши push-уведмоления!',
      'при подписках на блоги'
    ]
  }

  React.useEffect(() => {
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('buttons')
  }, [])

  let confirmed = useSignal(isConfirmed)

  const onNo = React.useCallback(() => {
    if (localStorage) {
      localStorage.setItem('notification_permission_prompted_' + reason, 'true')
    }
    onClose()
  }, [reason, onClose])

  const onYes = React.useCallback(async () => {
    confirmed.value = true
    await requestPermissions()
    onClose()
  }, [onClose])

  if (isConfirmed && cachedPermission.value !== 'denied') {
    React.useEffect(() => {
      // noinspection JSIgnoredPromiseFromCall
      onYes()
    }, [])
  }

  return <Modal
    onClose={onNo}
    showClose={!confirmed.value || cachedPermission.value === 'denied'}
    disableOutsideClose={confirmed.value && cachedPermission.value !== 'denied'}
  >
    {cachedPermission.value === 'denied' && <>
      <h1 className={styles['message']}>Получение уведомлений заблокировано :(</h1>
      <p>Чтобы получать уведомления на сайте, зайди в настройки браузера<br/>и разреши получение уведомлений с сайта StopGame.ru</p>
      <p>После этого надо будет перезагрузить страницу.</p>
      <button type='button' className={buttonsCss['primary']} onClick={onClose} autoFocus={true}>Хорошо</button>
    </>}
    {!confirmed.value && cachedPermission.value !== 'denied' && <>
      <h1 className={styles['message']}>{text[reason][0]}</h1>
      <p>{text[reason][1]}</p>
      <p>Никакого спама, только то, что тебе интересно. Обещаем! ;)</p>
      <p style='color: var(--gray-2); font-size: 14px'>
        В случае отказа мы больше не будем спрашивать тебя {text[reason][2]} в этом браузере
      </p>
      <div className={styles['buttons']}>
        <button onClick={onNo} className={buttonsCss['low-contrast']} autoFocus={true}>Нет</button>
        <button onClick={onYes} className={buttonsCss['primary']}>Включить</button>
      </div>
    </>}
    {confirmed.value && cachedPermission.value !== 'denied' && <>
      <h1 className={styles['message']} style={{ marginBottom: 0 }}>Разреши получение уведомлений в браузере</h1>
    </>}
  </Modal>
}

export const showNotificationRequestModal = (reason: NotificationPermissionRequestType, isConfirmed = false) => {
  return new Promise((resolve) => {
    showModal(NotificationRequestModal, {
      reason,
      isConfirmed
    }, () => {
      resolve(true)
    })
  })
}

export const promptNotificationsPermission = async (type: NotificationPermissionRequestType) => {
  if (await getPermission() !== 'default') {
    return
  }
  if (localStorage && localStorage.getItem('notification_permission_prompted_' + type) === 'true') {
    return
  }

  await showNotificationRequestModal(type)
}

window.promptNotificationsPermission = promptNotificationsPermission

export const initMessaging = (registration: ServiceWorkerRegistration) => {
  SWregistration = registration
  getPermission()
    .then(async (permission) => {
      if (permission === 'allowed') {
        await fetchAndSaveToken()
      }
    })
}
